import React, { useState, useEffect, useRef } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Plus, Pencil, Trash2, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../ui/RichTextEditor';

export const AdminPages = () => {
  const { apiCall } = useAdmin();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: { ro: '', bg: '', en: '' },
    slug: '',
    content: { ro: '', bg: '', en: '' },
    is_published: true,
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const data = await apiCall('/admin/pages');
      setPages(data || []);
    } catch (err) {
      toast.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await apiCall(`/admin/pages/${editingPage.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Page updated');
      } else {
        await apiCall('/admin/pages', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Page created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPages();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await apiCall(`/admin/pages/${id}`, { method: 'DELETE' });
      toast.success('Page deleted');
      fetchPages();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title || { ro: '', bg: '', en: '' },
      slug: page.slug || '',
      content: page.content || { ro: '', bg: '', en: '' },
      is_published: page.is_published ?? true,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPage(null);
    setFormData({
      title: { ro: '', bg: '', en: '' },
      slug: '',
      content: { ro: '', bg: '', en: '' },
      is_published: true,
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-pages">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Pages</h2>
          <p className="text-muted-foreground">Manage static pages</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Page</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Edit Page' : 'Add Page'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Title (RO)</Label><Input value={formData.title.ro} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ro: e.target.value } })} required /></div>
                <div><Label>Title (BG)</Label><Input value={formData.title.bg} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, bg: e.target.value } })} /></div>
                <div><Label>Title (EN)</Label><Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} /></div>
              </div>
              <div><Label>Slug</Label><Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required /></div>
              <div><Label>Content (RO)</Label><Textarea value={formData.content.ro} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, ro: e.target.value } })} rows={6} /></div>
              <div><Label>Content (EN)</Label><Textarea value={formData.content.en} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, en: e.target.value } })} rows={6} /></div>
              <label className="flex items-center gap-2"><Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })} /><span>Published</span></label>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingPage ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : pages.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No pages yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4">Title</th>
                  <th className="text-left py-3 px-4">Slug</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr></thead>
                <tbody>
                  {pages.map((page) => (
                    <tr key={page.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{page.title?.en || page.title?.ro}</td>
                      <td className="py-3 px-4 text-muted-foreground">/{page.slug}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${page.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {page.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(page)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(page.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const AdminBlog = () => {
  const { apiCall } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: { ro: '', en: '' },
    slug: '',
    content: { ro: '', en: '' },
    excerpt: { ro: '', en: '' },
    featured_image: '',
    is_published: true,
    tags: [],
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const data = await apiCall('/admin/blog');
      setPosts(data || []);
    } catch (err) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const token = localStorage.getItem('rani_admin_token');
      const response = await fetch(`${API}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setFormData(prev => ({ ...prev, featured_image: data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await apiCall(`/admin/blog/${editingPost.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Post updated');
      } else {
        await apiCall('/admin/blog', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Post created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await apiCall(`/admin/blog/${id}`, { method: 'DELETE' });
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || { ro: '', en: '' },
      slug: post.slug || '',
      content: post.content || { ro: '', en: '' },
      excerpt: post.excerpt || { ro: '', en: '' },
      featured_image: post.featured_image || '',
      is_published: post.is_published ?? true,
      tags: post.tags || [],
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: { ro: '', en: '' },
      slug: '',
      content: { ro: '', en: '' },
      excerpt: { ro: '', en: '' },
      featured_image: '',
      is_published: true,
      tags: [],
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Blog</h2>
          <p className="text-muted-foreground">Manage blog posts</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Post</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit Post' : 'Add Post'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Title (RO)</Label><Input value={formData.title.ro} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ro: e.target.value } })} required /></div>
                <div><Label>Title (EN)</Label><Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} /></div>
              </div>
              <div><Label>Slug</Label><Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required /></div>
              
              {/* Image Upload */}
              <div>
                <Label>Featured Image</Label>
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full text-sm border rounded-lg p-2"
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
                  </div>
                  {formData.featured_image && (
                    <img src={formData.featured_image.startsWith('/api') ? `${API}${formData.featured_image}` : formData.featured_image} alt="Preview" className="w-20 h-20 object-cover rounded" />
                  )}
                </div>
              </div>

              <div><Label>Excerpt (RO)</Label><Textarea value={formData.excerpt.ro} onChange={(e) => setFormData({ ...formData, excerpt: { ...formData.excerpt, ro: e.target.value } })} rows={2} /></div>
              <div><Label>Content (RO)</Label><Textarea value={formData.content.ro} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, ro: e.target.value } })} rows={6} placeholder="Use paragraphs with blank lines between them for proper formatting" /></div>
              <div><Label>Content (EN)</Label><Textarea value={formData.content.en} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, en: e.target.value } })} rows={6} /></div>
              <label className="flex items-center gap-2"><Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })} /><span>Published</span></label>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingPost ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : posts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No posts yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4">Title</th>
                  <th className="text-left py-3 px-4">Slug</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr></thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{post.title?.en || post.title?.ro}</td>
                      <td className="py-3 px-4 text-muted-foreground">/blog/{post.slug}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${post.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {post.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(post)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(post.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const AdminSliders = () => {
  const { apiCall, API } = useAdmin();
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: { ro: '', en: '' },
    subtitle: { ro: '', en: '' },
    image: '',
    link: '',
    button_text: { ro: '', en: '' },
    button_link: '',
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const data = await apiCall('/admin/sliders');
      setSliders(data || []);
    } catch (err) {
      toast.error('Failed to fetch sliders');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('files', files[0]);

      const token = localStorage.getItem('rani_admin_token');
      const response = await fetch(`${API}/upload/images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      if (data.uploaded && data.uploaded.length > 0) {
        setFormData({ ...formData, image: data.uploaded[0].url });
        toast.success('Image uploaded');
      }
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlider) {
        await apiCall(`/admin/sliders/${editingSlider.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Slider updated');
      } else {
        await apiCall('/admin/sliders', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Slider created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSliders();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      await apiCall(`/admin/sliders/${deleteConfirmId}`, { method: 'DELETE' });
      toast.success('Slider deleted');
      fetchSliders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setFormData({
      title: slider.title || { ro: '', en: '' },
      subtitle: slider.subtitle || { ro: '', en: '' },
      image: slider.image || '',
      link: slider.link || '',
      button_text: slider.button_text || { ro: '', en: '' },
      button_link: slider.button_link || slider.link || '',
      is_active: slider.is_active ?? true,
      sort_order: slider.sort_order || 0,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSlider(null);
    setFormData({
      title: { ro: '', en: '' },
      subtitle: { ro: '', en: '' },
      image: '',
      link: '',
      button_text: { ro: '', en: '' },
      button_link: '',
      is_active: true,
      sort_order: 0,
    });
  };

  const getImageUrl = (url) => {
    if (url?.startsWith('/api')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return url;
  };

  return (
    <div className="space-y-6" data-testid="admin-sliders">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Sliders</h2>
          <p className="text-muted-foreground">Manage homepage hero sliders</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Slider</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSlider ? 'Edit Slider' : 'Add Slider'}</DialogTitle>
              <DialogDescription>
                Configure the homepage slider with image, titles, and button
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Slider Image *</Label>
                {formData.image ? (
                  <div className="relative">
                    <img 
                      src={getImageUrl(formData.image)} 
                      alt="Slider preview" 
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({ ...formData, image: '' })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-muted-foreground">Click to upload slider image</p>
                    <p className="text-xs text-muted-foreground mt-1">Recommended: 1920x800px</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files)}
                  className="hidden"
                />
                {uploading && (
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Uploading...</span>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Or enter image URL directly</Label>
                  <Input 
                    value={formData.image} 
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })} 
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Title (Main Heading)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Romanian (RO)</Label>
                    <Input 
                      value={formData.title.ro} 
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ro: e.target.value } })} 
                      placeholder="Lenjerii de pat premium"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">English (EN)</Label>
                    <Input 
                      value={formData.title.en} 
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} 
                      placeholder="Premium Bed Linens"
                    />
                  </div>
                </div>
              </div>

              {/* Subtitle */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Subtitle (Description)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Romanian (RO)</Label>
                    <Textarea 
                      value={formData.subtitle?.ro || ''} 
                      onChange={(e) => setFormData({ ...formData, subtitle: { ...formData.subtitle, ro: e.target.value } })} 
                      placeholder="Descoperă colecția noastră..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">English (EN)</Label>
                    <Textarea 
                      value={formData.subtitle?.en || ''} 
                      onChange={(e) => setFormData({ ...formData, subtitle: { ...formData.subtitle, en: e.target.value } })} 
                      placeholder="Discover our collection..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Button */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Button</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Button Text (RO)</Label>
                    <Input 
                      value={formData.button_text?.ro || ''} 
                      onChange={(e) => setFormData({ ...formData, button_text: { ...formData.button_text, ro: e.target.value } })} 
                      placeholder="Cumpără acum"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Button Text (EN)</Label>
                    <Input 
                      value={formData.button_text?.en || ''} 
                      onChange={(e) => setFormData({ ...formData, button_text: { ...formData.button_text, en: e.target.value } })} 
                      placeholder="Shop now"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Button Link</Label>
                  <Input 
                    value={formData.button_link || formData.link} 
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value, link: e.target.value })} 
                    placeholder="/shop or /shop?category=..."
                  />
                </div>
              </div>

              {/* Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sort Order</Label>
                  <Input 
                    type="number" 
                    value={formData.sort_order} 
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} 
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch 
                      checked={formData.is_active} 
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} 
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingSlider ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : sliders.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No sliders yet. Add your first slider!</div>
          ) : (
            <div className="divide-y">
              {sliders.map((slider, index) => (
                <div key={slider.id} className="flex gap-4 p-4 hover:bg-gray-50">
                  <div className="flex-shrink-0">
                    <img 
                      src={getImageUrl(slider.image)} 
                      alt="" 
                      className="w-48 h-28 object-cover rounded-lg border"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/300x150?text=No+Image'; }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-lg">
                          {slider.title?.ro || slider.title?.en || 'Untitled Slider'}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {slider.subtitle?.ro || slider.subtitle?.en || 'No description'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          slider.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {slider.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-muted-foreground">#{index + 1}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      {slider.button_text?.ro && (
                        <span className="text-muted-foreground">
                          Button: <span className="text-primary">{slider.button_text.ro}</span>
                        </span>
                      )}
                      {slider.link && (
                        <span className="text-muted-foreground">
                          Link: <span className="font-mono text-xs">{slider.link}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(slider)}>
                        <Pencil className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:text-red-600"
                        onClick={() => setDeleteConfirmId(slider.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Slider</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this slider? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const AdminCoupons = () => {
  const { apiCall } = useAdmin();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: 0,
    min_order_value: 0,
    max_uses: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const data = await apiCall('/admin/coupons');
      setCoupons(data || []);
    } catch (err) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiCall('/admin/coupons', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      toast.success('Coupon created');
      setIsDialogOpen(false);
      setFormData({ code: '', discount_type: 'percent', discount_value: 0, min_order_value: 0, max_uses: 0, is_active: true });
      fetchCoupons();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await apiCall(`/admin/coupons/${id}`, { method: 'DELETE' });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Coupons</h2>
          <p className="text-muted-foreground">Manage discount codes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Coupon</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Coupon</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Code</Label><Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type</Label>
                  <select className="w-full h-10 px-3 border rounded-lg" value={formData.discount_type} onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}>
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div><Label>Value</Label><Input type="number" value={formData.discount_value} onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Min Order Value</Label><Input type="number" value={formData.min_order_value} onChange={(e) => setFormData({ ...formData, min_order_value: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Max Uses (0 = unlimited)</Label><Input type="number" value={formData.max_uses} onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <label className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} /><span>Active</span></label>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : coupons.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No coupons yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4">Code</th>
                  <th className="text-left py-3 px-4">Discount</th>
                  <th className="text-left py-3 px-4">Min Order</th>
                  <th className="text-left py-3 px-4">Used</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr></thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono font-medium">{coupon.code}</td>
                      <td className="py-3 px-4">{coupon.discount_value}{coupon.discount_type === 'percent' ? '%' : ' RON'}</td>
                      <td className="py-3 px-4">{coupon.min_order_value || '-'}</td>
                      <td className="py-3 px-4">{coupon.used_count || 0}{coupon.max_uses ? `/${coupon.max_uses}` : ''}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {coupon.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(coupon.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Footer Pages Management
export const AdminFooterPages = () => {
  const { apiCall } = useAdmin();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: { ro: '', en: '' },
    slug: '',
    content: { ro: '', en: '' },
    page_type: 'information',
    is_published: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const data = await apiCall('/admin/footer-pages');
      setPages(data || []);
    } catch (err) {
      toast.error('Failed to fetch footer pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await apiCall(`/admin/footer-pages/${editingPage.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Page updated');
      } else {
        await apiCall('/admin/footer-pages', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Page created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPages();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await apiCall(`/admin/footer-pages/${id}`, { method: 'DELETE' });
      toast.success('Page deleted');
      fetchPages();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title || { ro: '', en: '' },
      slug: page.slug || '',
      content: page.content || { ro: '', en: '' },
      page_type: page.page_type || 'information',
      is_published: page.is_published ?? true,
      sort_order: page.sort_order || 0,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPage(null);
    setFormData({
      title: { ro: '', en: '' },
      slug: '',
      content: { ro: '', en: '' },
      page_type: 'information',
      is_published: true,
      sort_order: 0,
    });
  };

  const pageTypeLabels = {
    information: 'Information',
    ranistore: 'RaniStore',
    other: 'Other',
  };

  return (
    <div className="space-y-6" data-testid="admin-footer-pages">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Footer Pages</h2>
          <p className="text-muted-foreground">Manage footer navigation pages (Information, RaniStore columns)</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Page</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Edit Footer Page' : 'Add Footer Page'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title (RO)</Label>
                  <Input value={formData.title.ro} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ro: e.target.value } })} required />
                </div>
                <div>
                  <Label>Title (EN)</Label>
                  <Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Slug (URL)</Label>
                  <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required placeholder="payment-methods" />
                </div>
                <div>
                  <Label>Category</Label>
                  <select
                    value={formData.page_type}
                    onChange={(e) => setFormData({ ...formData, page_type: e.target.value })}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="information">Information (Column 1)</option>
                    <option value="ranistore">RaniStore (Column 2)</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Content (RO)</Label>
                <RichTextEditor 
                  value={formData.content.ro} 
                  onChange={(html) => setFormData({ ...formData, content: { ...formData.content, ro: html } })}
                  placeholder="Conținut pagină în română..."
                />
              </div>
              <div>
                <Label>Content (EN)</Label>
                <RichTextEditor 
                  value={formData.content.en} 
                  onChange={(html) => setFormData({ ...formData, content: { ...formData.content, en: html } })}
                  placeholder="Page content in English..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sort Order</Label>
                  <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })} />
                    <span>Published</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingPage ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : pages.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No footer pages yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4">Title</th>
                    <th className="text-left py-3 px-4">Slug</th>
                    <th className="text-left py-3 px-4">Category</th>
                    <th className="text-left py-3 px-4">Order</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((page) => (
                    <tr key={page.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{page.title?.ro || page.title?.en}</td>
                      <td className="py-3 px-4 text-muted-foreground font-mono text-xs">{page.slug}</td>
                      <td className="py-3 px-4">{pageTypeLabels[page.page_type] || page.page_type}</td>
                      <td className="py-3 px-4">{page.sort_order}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${page.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {page.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(page)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(page.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPages;
